import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  VStack,
  Box,
  Image,
  Heading,
} from '@chakra-ui/react';
import appBranding from '@happylife-a/branding';
import webCore from '@happylife-a/web-core';

const AGE_VERIFICATION_KEY = 'ageVerified';

export default function AgeGate({ children }) {
  const { t } = webCore.contexts.useTranslation();
  const [isVerified, setIsVerified] = useState(null); // null = loading, true = verified, false = denied
  const [showModal, setShowModal] = useState(false);

  const ageRestricted = appBranding.configs.environments.getField(
    appBranding.fields.brand.FeatureFlag.AgeRestricted
  );

  const logoUrl = appBranding.configs.assets.getAsset('logos/png/original-colored.png');

  useEffect(() => {
    // If age restriction is not enabled, bypass the gate
    if (!ageRestricted) {
      setIsVerified(true);
      return;
    }

    // Bypass age gate for prerender/bot crawlers so pages can be cached
    const isPrerender = /HeadlessChrome|Prerender/i.test(navigator.userAgent);
    if (isPrerender) {
      setIsVerified(true);
      return;
    }

    // Check storage for existing verification
    (async () => {
      const storedVerification = await webCore.helpers.storage.getItem(AGE_VERIFICATION_KEY);

      if (storedVerification === true || storedVerification === 'true') {
        setIsVerified(true);
      } else if (storedVerification === false || storedVerification === 'false') {
        setIsVerified(false);
      } else {
        // No stored value, show modal
        setShowModal(true);
      }
    })();
  }, [ageRestricted]);

  const handleConfirm = async () => {
    await webCore.helpers.storage.setItem(AGE_VERIFICATION_KEY, true);
    setIsVerified(true);
    setShowModal(false);
  };

  const handleDeny = async () => {
    await webCore.helpers.storage.setItem(AGE_VERIFICATION_KEY, false);
    setIsVerified(false);
    setShowModal(false);
  };

  // Prevent scrolling when access is denied
  useEffect(() => {
    if (isVerified === false) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'unset';
      };
    }
  }, [isVerified]);

  // User confirmed they are 18+
  if (isVerified === true) {
    return <>{children}</>;
  }

  // User denied or is under 18
  if (isVerified === false) {
    return (
      <Box
        position="fixed"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="gray.50"
        display="flex"
        alignItems="center"
        justifyContent="center"
        zIndex={9999}
        overflow="hidden"
        px={4}
      >
        <Box
          bg="white"
          borderRadius="16px"
          boxShadow="lg"
          p={{ base: 6, md: 10 }}
          maxW="640px"
          w="100%"
          textAlign="center"
        >
          <VStack spacing={6}>
            <Image src={logoUrl} alt="Brand Logo" maxH="70px" mx="auto" objectFit="contain" />
            <Heading as="h2" size="lg">
              {t('Access Restricted')}
            </Heading>
            <Text fontSize="md" color="gray.700">
              {t('You must be 18 years or older to access this site.')}
            </Text>
            <Text fontSize="sm" color="gray.500">
              {t('If you made a mistake, please clear your browser data and try again.')}
            </Text>
          </VStack>
        </Box>
      </Box>
    );
  }

  // Show age verification modal
  return (
    <>
      <Modal
        isOpen={showModal}
        onClose={() => {}}
        closeOnOverlayClick={false}
        closeOnEsc={false}
        isCentered
        size="md"
      >
        <ModalOverlay bg="blackAlpha.800" backdropFilter="blur(10px)" />
        <ModalContent>
          <ModalHeader textAlign="center" pt={6}>
            <VStack spacing={4}>
              <Image src={logoUrl} alt="Brand Logo" maxH="60px" objectFit="contain" mx="auto" />
              <Heading as="h3" size="md">
                {t('Age Verification')}
              </Heading>
            </VStack>
          </ModalHeader>
          <ModalBody>
            <Text fontSize="md" color="gray.700" textAlign="center">
              {t('You must be 18 years or older to access this website.')}
            </Text>
          </ModalBody>
          <ModalFooter justifyContent="center" gap={4} pb={6}>
            <Button
              variant="filledBig"
              _hover={{ opacity: 0.9 }}
              size="lg"
              onClick={handleConfirm}
              px={8}
            >
              {t('Yes, I am 18+')}
            </Button>
            <Button variant="defaultBig" size="lg" onClick={handleDeny} px={8}>
              {t('No, I am under 18')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
